import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apportionmentMessages } from 'pages/admin/demand/apportionment/variables/apportionmentMessages';
import { PlanningTypes } from 'pages/admin/demand/planning/context/types/contextPrevision.type';
import alb from 'services/api/alb';
import api from 'services/api/api';

import {
	buildApportionmentSetup,
	ISaveSetupParams,
} from '../../../../../apportionment/hooks/useDefaultSetup';
import { IFilterHierarchy } from '../types/planning.types';
import { buildFiltersPayload } from '../utils/planningByLevelUtils';

export type PrevisionItem = {
	monthIndex: number;
	year: number;
	quantity: number;
};

export enum EmptyPredictionAction {
	SAVE_ZERO = 'save_zero',
	DISTRIBUTE_EQUALLY = 'distribute_equally',
}

export interface ISavePlanning {
	planningType: PlanningTypes;
	cycleId: number;
	setup?: ISaveSetupParams;
	companyAreaId?: number;
	actionForEmptyPrediction?: EmptyPredictionAction;
	previsions: PrevisionItem[];
	filters?: IFilterHierarchy;
	hasManualInput?: boolean;
}

export const saveSetup = async (params: ISavePlanning): Promise<true> => {
	const {
		planningType,
		cycleId,
		companyAreaId,
		setup,
		actionForEmptyPrediction,
		previsions,
		filters,
	} = params;
	let apportionment_setup;

	if (setup) {
		apportionment_setup = buildApportionmentSetup(setup);
	}

	const url = `/${planningType}`;
	const response = await alb.post(url, {
		previsions: previsions.map(pre => ({
			quantity: pre.quantity,
			month_year: new Date(pre.year, pre.monthIndex, 1).toISOString(),
		})),
		apportionment_setup,
		action_for_empty_prediction: actionForEmptyPrediction,
		...buildFiltersPayload({ cycleId, filters, companyAreaId }),
	});

	return response.data;
};

export const useSavePlanning = (
	onSuccess: VoidFunction,
	onErrorZeroApportionmentDistribution: VoidFunction
) => {
	const toast = useToast();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: saveSetup,
		onSuccess: async (
			_,
			{ planningType, cycleId, filters, companyAreaId, setup }
		) => {
			onSuccess();
			toast({
				position: 'bottom-left',
				title: apportionmentMessages.MSG_PLAN_DISTRIBUTED_SUCCESS,
				status: 'success',
				duration: 3000,
				isClosable: true,
			});

			queryClient.invalidateQueries({
				queryKey: ['planning', planningType],
			});
			queryClient.invalidateQueries({
				queryKey: [`is-status-editable`, planningType],
			});
			queryClient.invalidateQueries({
				queryKey: [
					'is-complete',
					planningType,
					cycleId,
					companyAreaId || '',
				],
			});
			queryClient.invalidateQueries({
				queryKey: ['comparative-summary', planningType, cycleId],
			});
			if (setup?.saveAsDefault) {
				queryClient.invalidateQueries({
					queryKey: [
						'defaultSetup',
						planningType,
						cycleId,
						companyAreaId || '',
					],
				});
			}
			await queryClient.fetchQuery({
				queryKey: ['planning', planningType, cycleId, filters],
				queryFn: () => {
					const url = `/${planningType}/find`;
					return api
						.post(
							url,
							buildFiltersPayload({
								cycleId,
								filters,
								companyAreaId,
							})
						)
						.then(response => response.data.rows);
				},
			});
		},
		onError: (error: any) => {
			if (error.status === 428) {
				onErrorZeroApportionmentDistribution();
			} else {
				toast({
					position: 'bottom-left',
					title: apportionmentMessages.MSG_PLAN_DISTRIBUTED_ERROR,
					status: 'error',
					duration: 3000,
					isClosable: true,
				});
			}
		},
	});
};
